import type { FC, ReactNode } from 'react'
import type { TargetResolver } from '@/components/three/device-canvas'

import dynamic from 'next/dynamic'
import { useCallback, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { DeviceFallback } from '@/components/three/device-fallback'

import {
    DEVICE_ASPECT,
    clamp01,
    lerp,
    smoothstep
} from '@/components/three/device-constants'

const DeviceCanvas = dynamic(
    () => import('@/components/three/device-canvas').then(module => module.DeviceCanvas),
    { ssr: false }
)

interface ProductDeviceProps {
    className?: string
    children?: ReactNode
}

/**
 * Self-contained 3D device for inner pages. It turns as its section
 * scrolls through the viewport and follows the pointer.
 */
export const ProductDevice: FC<ProductDeviceProps> = ({ className, children }): ReactNode => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [fallback, setFallback] = useState(false)

    const resolver = useRef<TargetResolver>((canvas, reducedMotion) => {
        const rect = canvas.getBoundingClientRect()
        const viewport = window.innerHeight || 1
        const progress = reducedMotion ? 0.35 : clamp01((viewport - rect.top) / (viewport + rect.height))
        const eased = smoothstep(progress)

        return {
            x: rect.width / 2,
            y: rect.height * 0.46,
            width: Math.min(rect.width * 0.7, rect.height * DEVICE_ASPECT * 0.6),
            rotationX: lerp(0.42, -0.06, eased),
            rotationY: lerp(-0.95, 0.55, eased),
            rotationZ: lerp(0.08, -0.03, eased),
            depth: 0,
            scale: 1,
            idle: 1,
            shadow: 1
        }
    })

    const showFallback = useCallback(() => setFallback(true), [])

    return (
        <div ref={containerRef} className={cn('relative aspect-[5/4] w-full', className)}>
            {fallback ? (
                <div className='absolute inset-0 flex items-center justify-center p-10'>
                    <DeviceFallback className='max-w-[420px]' />
                </div>
            ) : (
                <div className='absolute inset-0'>
                    <DeviceCanvas resolver={resolver} observe={containerRef} onFallback={showFallback} />
                </div>
            )}

            {children}
        </div>
    )
}
